/**
 * TermsPage.tsx — /#/terms
 */
import { useNavigate } from 'react-router-dom'

const GOLD = '#c9a84c'
const NAVY = '#0a1628'

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <div style={{ marginBottom: 40 }}>
    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#f1f5f9', marginBottom: 12 }}>{title}</h2>
    <div style={{ color: '#94a3b8', lineHeight: 1.8, fontSize: 15 }}>{children}</div>
  </div>
)

export default function TermsPage() {
  const navigate = useNavigate()
  return (
    <div style={{ background: NAVY, color: '#e2e8f0', fontFamily: "'Segoe UI', system-ui, sans-serif", minHeight: '100vh' }}>
      <nav style={{ background: 'rgba(10,22,40,0.95)', backdropFilter: 'blur(12px)', borderBottom: '1px solid rgba(201,168,76,0.15)', display: 'flex', alignItems: 'center', padding: '0 32px', height: 60, position: 'sticky', top: 0, zIndex: 100 }}>
        <div onClick={() => navigate('/')} style={{ fontSize: 20, fontWeight: 800, color: GOLD, letterSpacing: 2, flex: 1, cursor: 'pointer' }}>CASTELLAN</div>
        <span onClick={() => navigate('/login')} style={{ color: '#94a3b8', cursor: 'pointer', fontSize: 14 }}>Sign in</span>
      </nav>

      <div style={{ maxWidth: 720, margin: '0 auto', padding: '64px 24px' }}>
        <h1 style={{ fontSize: 40, fontWeight: 800, color: '#f1f5f9', marginBottom: 8 }}>Terms of Service</h1>
        <p style={{ color: '#475569', marginBottom: 48 }}>Last updated: May 10, 2026</p>

        <Section title="1. Acceptance">
          By creating an account or using Castellan Security ("the Service"), you agree to these Terms of Service. If you do not agree, do not use the Service. These terms apply to all users, administrators, and tenants of the platform.
        </Section>

        <Section title="2. Description of service">
          Castellan Security is a cloud-based security operations management platform providing shift scheduling, equipment tracking, personnel management, and related tools for security teams. The Service is provided on a subscription basis.
        </Section>

        <Section title="3. Accounts and access">
          <ul style={{ paddingLeft: 20 }}>
            <li style={{ marginBottom: 8 }}>You are responsible for maintaining the confidentiality of your credentials.</li>
            <li style={{ marginBottom: 8 }}>You must provide accurate information during registration.</li>
            <li style={{ marginBottom: 8 }}>One account per individual. Sharing credentials between multiple individuals is prohibited.</li>
            <li style={{ marginBottom: 8 }}>You are responsible for all activity that occurs under your account.</li>
          </ul>
        </Section>

        <Section title="4. Acceptable use">
          <p style={{ marginBottom: 12 }}>You agree not to:</p>
          <ul style={{ paddingLeft: 20 }}>
            <li style={{ marginBottom: 8 }}>Use the Service for any unlawful purpose or in violation of any applicable law</li>
            <li style={{ marginBottom: 8 }}>Attempt to gain unauthorized access to any part of the Service or its underlying infrastructure</li>
            <li style={{ marginBottom: 8 }}>Reverse engineer, decompile, or disassemble any part of the Service</li>
            <li style={{ marginBottom: 8 }}>Transmit any malicious code, viruses, or destructive data</li>
            <li style={{ marginBottom: 8 }}>Use the Service to harass, threaten, or harm any person</li>
            <li style={{ marginBottom: 8 }}>Resell or sublicense access to the Service without written permission</li>
          </ul>
        </Section>

        <Section title="5. Subscriptions and billing">
          <ul style={{ paddingLeft: 20 }}>
            <li style={{ marginBottom: 8 }}>Subscriptions are billed monthly in advance. Prices are listed on the pricing page and may change with 30 days notice.</li>
            <li style={{ marginBottom: 8 }}>The Shift tier includes a 14-day free trial. No charge is made during the trial period.</li>
            <li style={{ marginBottom: 8 }}>You may cancel at any time. Access continues until the end of the paid billing period.</li>
            <li style={{ marginBottom: 8 }}>Refunds are not provided for partial billing periods.</li>
            <li style={{ marginBottom: 8 }}>Failed payments result in a grace period followed by suspension if unresolved.</li>
            <li style={{ marginBottom: 8 }}>All billing is processed through Stripe. Stripe's terms apply to payment processing.</li>
          </ul>
        </Section>

        <Section title="6. Data ownership">
          You retain ownership of all data you enter into the Service. We do not claim ownership of your operational data, shift records, or personnel information. You grant us a limited license to store and process your data solely to provide the Service.
        </Section>

        <Section title="7. Service availability (SLA)">
          <ul style={{ paddingLeft: 20 }}>
            <li style={{ marginBottom: 8 }}><strong style={{ color: '#f1f5f9' }}>Shift tier:</strong> Best-effort availability. No uptime guarantee.</li>
            <li style={{ marginBottom: 8 }}><strong style={{ color: '#f1f5f9' }}>Terrain+NFC tier:</strong> 99.5% monthly uptime target.</li>
            <li style={{ marginBottom: 8 }}><strong style={{ color: '#f1f5f9' }}>Command tier:</strong> 99.9% monthly uptime target with priority incident response.</li>
          </ul>
          <p style={{ marginTop: 12 }}>Scheduled maintenance windows will be communicated at least 24 hours in advance.</p>
        </Section>

        <Section title="8. Termination">
          We may suspend or terminate your account if you violate these Terms, fail to pay, or engage in activity that we reasonably believe harms the Service or other users. You may terminate your account at any time by canceling your subscription and contacting support.
        </Section>

        <Section title="9. Limitation of liability">
          To the fullest extent permitted by law, Castellan Security shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of the Service. Our total liability for any claim shall not exceed the amount you paid in the 3 months preceding the claim.
        </Section>

        <Section title="10. Disclaimer of warranties">
          The Service is provided "as is" without warranty of any kind. We do not warrant that the Service will be uninterrupted, error-free, or suitable for your specific purpose.
        </Section>

        <Section title="11. Governing law">
          These Terms are governed by the laws of the Commonwealth of Virginia, United States, without regard to conflict of law principles. Disputes shall be resolved in the courts of Fairfax County, Virginia.
        </Section>

        <Section title="12. Changes to these terms">
          We may update these Terms from time to time. We will notify registered users of material changes at least 30 days before they take effect. Continued use of the Service constitutes acceptance.
        </Section>

        <Section title="13. Contact">
          Questions about these Terms should be directed to support@castellan-security.com.
        </Section>
      </div>
    </div>
  )
}
